import { Empty, Skeleton } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { IoChevronForward } from 'react-icons/io5'
import { TbLayoutGrid } from 'react-icons/tb'
import { Link } from 'react-router'

import { cx } from '@/Ship/Helpers/cssClass'
import { formatRecordsCount } from '@/Ship/Helpers/pluralize'

import { resolveDictionaryGroups, type DictionaryItem } from '../../Configs/dictionaries'
import { useListResources } from '../../Hooks'
import { dictionaryCountsStore } from '../../Stores/DictionaryCountsStore'

import styles from './DictionariesOverview.module.css'

/** Рядок стану під назвою: скелетон / «18 записів» / «Порожньо» / «—» при помилці */
const CountLine = observer(({ resourceKey }: { resourceKey: string }) => {
    const state = dictionaryCountsStore.get(resourceKey)
    if (state.status === 'loading') {
        return (
            <span className={cx(styles, 'skeleton')} role="status" aria-label="Кількість записів завантажується">
                <Skeleton.Input active size="small" />
            </span>
        )
    }
    if (state.status === 'error') {
        return (
            <span className={cx(styles, 'count')} title="Не вдалося отримати кількість">
                —
            </span>
        )
    }
    if (state.total === 0) return <span className={cx(styles, 'countEmpty')}>Порожньо</span>
    return <span className={cx(styles, 'count')}>{formatRecordsCount(state.total)}</span>
})

const Tile = ({ item }: { item: DictionaryItem }) => {
    const Icon = item.icon ?? TbLayoutGrid
    return (
        <Link to={item.pageUrl} className={cx(styles, 'tile')}>
            <span className={cx(styles, 'tileIcon')} aria-hidden="true">
                <Icon />
            </span>
            <span className={cx(styles, 'tileMain')}>
                <span className={cx(styles, 'tileTitle')}>{item.title}</span>
                <CountLine resourceKey={item.resourceKey} />
            </span>
            <IoChevronForward className={cx(styles, 'chevron')} aria-hidden="true" />
        </Link>
    )
}

/**
 * Огляд розділу «Довідники»: групи з плитками (іконка, назва, кількість записів).
 * Лічильники перечитуються при кожному монтуванні — після додавання записів число свіже.
 */
export const DictionariesOverview = () => {
    const resources = useListResources()
    const groups = useMemo(() => resolveDictionaryGroups(resources), [resources])

    useEffect(() => {
        void dictionaryCountsStore.load(
            groups.flatMap((group) => group.items.map(({ resourceKey, restUrl }) => ({ resourceKey, restUrl })))
        )
    }, [groups])

    if (groups.length === 0) {
        return (
            <div className={cx(styles, 'empty')}>
                <Empty description="Немає доступних довідників" />
            </div>
        )
    }

    return (
        <div className={cx(styles, 'overview')}>
            {groups.map((group) => (
                <section
                    key={group.key}
                    className={cx(styles, 'group')}
                    aria-labelledby={`dictionaries-group-${group.key}`}
                >
                    <h2 id={`dictionaries-group-${group.key}`} className={cx(styles, 'groupTitle')}>
                        {group.label}
                    </h2>
                    <div className={cx(styles, 'grid')}>
                        {group.items.map((item) => (
                            <Tile key={item.resourceKey} item={item} />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )
}
